import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Alert, DataGrid, Heading } from '@aaimplatform/ui';
import type { ColumnDef } from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/pe-fund-analytics';
import { FUND_STRATEGY_LABELS } from './pe-labels';
import { formatPercent } from '../lien/format';

// ---------------------------------------------------------------------------
// Illustrative reference data
//
// None of these figures is read by the PE Valuation Engine. The engine keeps
// its own advance-rate and sector tables, which differ from these, and returns
// the guidance it applied in each collateral assessment (Collateral Monitoring
// shows it beside the recommended advance rate). The page says so in its
// banner. Serving the engine's parameters here needs a pe-valuation endpoint,
// which is a spec change for the operator to authorize.
// ---------------------------------------------------------------------------

/** One advance-rate reference row (UI data, not an API shape). */
type AdvanceRateRowState = {
  strategy: components['schemas']['FundStrategy'];
  lowRate: number;
  highRate: number;
  notes: string;
};

/** Keyed on the generated FundStrategy union, so every row names a strategy the platform stores. */
const ADVANCE_RATE_REFERENCE: AdvanceRateRowState[] = [
  {
    strategy: 'large_cap_buyout',
    lowRate: 0.6,
    highRate: 0.75,
    notes: 'Established GPs with proven track records, diversified portfolios',
  },
  {
    strategy: 'mid_market_buyout',
    lowRate: 0.5,
    highRate: 0.65,
    notes: 'Moderate concentration risk, sector-dependent valuations',
  },
  {
    strategy: 'growth_equity',
    lowRate: 0.4,
    highRate: 0.55,
    notes: 'Higher growth volatility, pre-profit companies common',
  },
  {
    strategy: 'venture_capital',
    lowRate: 0.2,
    highRate: 0.35,
    notes: 'High failure rate, J-curve effect, illiquid positions',
  },
  {
    strategy: 'real_assets',
    lowRate: 0.5,
    highRate: 0.65,
    notes: 'Tangible collateral backing, appraisal-based valuations',
  },
  {
    strategy: 'secondary',
    lowRate: 0.55,
    highRate: 0.7,
    notes: 'Reduced J-curve, more mature cash flows',
  },
  {
    strategy: 'fund_of_funds',
    lowRate: 0.45,
    highRate: 0.6,
    notes: 'Diversification benefit offset by fee drag and opacity',
  },
];

/**
 * Collateral Health Alert tiers, as offsets above a position's recommended
 * advance rate. Deterioration is a tier change: the member and the FI are
 * alerted, the member cures by adding assets, paying down, or rebalancing with
 * the manager, there is no automatic sale, and RED hands the position to the FI.
 */
const CHA_TIER_REFERENCE: Array<{ tier: string; level: string; description: string; tone: string }> = [
  {
    tier: 'Green',
    level: 'Below advance rate + 10 points',
    description: 'Healthy. No action.',
    tone: 'bg-growth/10 text-growth',
  },
  {
    tier: 'Yellow',
    level: 'Advance rate + 10 points',
    description: 'Review. The member and your institution receive a Collateral Health Alert.',
    tone: 'bg-warning/10 text-warning',
  },
  {
    tier: 'Orange',
    level: 'Advance rate + 15 points',
    description:
      'Action needed. The member cures within the cure period by adding assets, paying down, or rebalancing with the manager.',
    tone: 'bg-warning/20 text-warning',
  },
  {
    tier: 'Red',
    level: 'Advance rate + 25 points',
    description: 'Handed to your institution, which decides the next step. There is no automatic sale.',
    tone: 'bg-error/10 text-error',
  },
];

const CURE_PERIOD_REFERENCE = '30 calendar days';

/** One sector reference row (UI data, not an API shape). */
type SectorRowState = {
  sector: string;
  medianEvRevenue: string;
  medianGrowth: string;
  privateDiscount: string;
};

/** Illustrative sector reference; the engine's NAV estimator carries its own table. */
const SECTOR_REFERENCE: SectorRowState[] = [
  { sector: 'Technology', medianEvRevenue: '8.2x', medianGrowth: '18.5%', privateDiscount: '20-30%' },
  { sector: 'Healthcare', medianEvRevenue: '4.8x', medianGrowth: '12.3%', privateDiscount: '15-25%' },
  { sector: 'Financial Services', medianEvRevenue: '3.5x', medianGrowth: '8.7%', privateDiscount: '10-20%' },
  { sector: 'Industrials', medianEvRevenue: '2.8x', medianGrowth: '6.2%', privateDiscount: '15-25%' },
  { sector: 'Consumer', medianEvRevenue: '3.2x', medianGrowth: '9.1%', privateDiscount: '20-30%' },
  { sector: 'Energy', medianEvRevenue: '2.1x', medianGrowth: '5.4%', privateDiscount: '10-20%' },
  { sector: 'Real Estate', medianEvRevenue: '6.5x', medianGrowth: '4.8%', privateDiscount: '5-15%' },
  { sector: 'Infrastructure', medianEvRevenue: '5.2x', medianGrowth: '3.9%', privateDiscount: '5-15%' },
];

/** The example scale: a 65% advance rate and the tier boundaries it implies. */
const EXAMPLE_ADVANCE = 0.65;
const EXAMPLE_YELLOW = EXAMPLE_ADVANCE + 0.1;
const EXAMPLE_ORANGE = EXAMPLE_ADVANCE + 0.15;
const EXAMPLE_RED = EXAMPLE_ADVANCE + 0.25;

/** A CSS width or offset for the example scale. */
const pct = (ratio: number) => `${Math.round(ratio * 100)}%`;

// Every rate on the page prints at one decimal place, the portal's percent
// rule (monitoring/display-format.ts). The advance-rate table printed whole
// percents and the Marketplace two decimals (UI run r3, fi-cons-2).

// The two reference tables are the kit DataGrid, as on the list pages: its
// header treatment and its monospace figures. They were hand-written <table>
// markup with a local header class (UI run r3, fi-cons-4). DataGrid draws no
// card, so each sits flush in its section; the rows are few, so no pagination.
const ADVANCE_RATE_COLUMNS: ColumnDef<AdvanceRateRowState, unknown>[] = [
  {
    id: 'strategy',
    header: 'Strategy',
    accessorFn: (row) => FUND_STRATEGY_LABELS[row.strategy],
    cell: ({ row }) => (
      <span className="text-sm font-sans font-medium text-primary leading-normal">
        {FUND_STRATEGY_LABELS[row.original.strategy]}
      </span>
    ),
  },
  {
    id: 'lowRate',
    header: 'Low rate',
    accessorFn: (row) => row.lowRate,
    meta: { align: 'center' },
    cell: ({ row }) => (
      <span className="text-sm font-mono text-primary tabular-nums leading-normal">
        {formatPercent(row.original.lowRate)}
      </span>
    ),
  },
  {
    id: 'highRate',
    header: 'High rate',
    accessorFn: (row) => row.highRate,
    meta: { align: 'center' },
    cell: ({ row }) => (
      <span className="text-sm font-mono text-primary tabular-nums leading-normal">
        {formatPercent(row.original.highRate)}
      </span>
    ),
  },
  {
    id: 'notes',
    header: 'Notes',
    accessorFn: (row) => row.notes,
    enableSorting: false,
    cell: ({ row }) => (
      <span className="text-sm font-sans text-secondary leading-normal">{row.original.notes}</span>
    ),
  },
];

const SECTOR_COLUMNS: ColumnDef<SectorRowState, unknown>[] = [
  {
    id: 'sector',
    header: 'Sector',
    accessorFn: (row) => row.sector,
    cell: ({ row }) => (
      <span className="text-sm font-sans font-medium text-primary leading-normal">{row.original.sector}</span>
    ),
  },
  {
    id: 'medianEvRevenue',
    header: 'Median EV/Revenue',
    accessorFn: (row) => row.medianEvRevenue,
    meta: { align: 'center' },
    cell: ({ row }) => (
      <span className="text-sm font-mono text-primary tabular-nums leading-normal">
        {row.original.medianEvRevenue}
      </span>
    ),
  },
  {
    id: 'medianGrowth',
    header: 'Median growth',
    accessorFn: (row) => row.medianGrowth,
    meta: { align: 'center' },
    cell: ({ row }) => (
      <span className="text-sm font-mono text-primary tabular-nums leading-normal">
        {row.original.medianGrowth}
      </span>
    ),
  },
  {
    id: 'privateDiscount',
    header: 'Private discount',
    accessorFn: (row) => row.privateDiscount,
    meta: { align: 'center' },
    enableSorting: false,
    cell: ({ row }) => (
      <span className="text-sm font-mono text-secondary tabular-nums leading-normal">
        {row.original.privateDiscount}
      </span>
    ),
  },
];

// ---------------------------------------------------------------------------
// Expandable Section Component
// ---------------------------------------------------------------------------

// Internal: not an API contract
interface SectionProps {
  id: string;
  title: string;
  description: string;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

/**
 * A disclosure section. The toggle is a button inside the heading, so the
 * heading stays in the document outline and the button carries aria-expanded.
 */
function ConfigSection({ id, title, description, isExpanded, onToggle, children }: SectionProps) {
  const panelId = `${id}-panel`;
  return (
    <section className="bg-surface rounded-sm shadow-sm overflow-hidden">
      {/* Card scale, as the collateral pages size a level-2 heading. Level 2
          defaults to the marketing section scale, about 40px here (UI run r3,
          fi-g5-5). */}
      <Heading level={2} size="card">
        <button
          type="button"
          className="w-full flex items-center justify-between gap-4 px-6 pt-5 pb-1 text-left hover:bg-elevated transition-colors"
          onClick={onToggle}
          aria-expanded={isExpanded}
          aria-controls={panelId}
        >
          <span>{title}</span>
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-tertiary flex-shrink-0" aria-hidden="true" />
          ) : (
            <ChevronRight className="w-5 h-5 text-tertiary flex-shrink-0" aria-hidden="true" />
          )}
        </button>
      </Heading>
      <p className="px-6 pb-5 text-sm font-sans text-tertiary">{description}</p>
      {isExpanded && (
        <div id={panelId} className="border-t border-recessed">
          {children}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// PE Configuration Page
// ---------------------------------------------------------------------------

/**
 * PeConfigurationPage
 *
 * Read-only PE reference material in expandable sections: advance-rate ranges
 * by strategy, the Collateral Health Alert tiers, and sector benchmarks. All of
 * it is illustrative; none of it is a parameter the PE Valuation Engine reads.
 */
export function PeConfigurationPage() {
  const [expanded, setExpanded] = useState<Set<string>>(new Set(['advance-rates']));

  const toggleSection = (section: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  return (
    <div>
      <div className="mb-6">
        {/* Page title lives in the sticky topnav (AppHeader); no redundant H1. */}
        <p className="text-sm font-sans text-tertiary">Private equity valuation reference material</p>
      </div>

      <Alert type="warning" title="Illustrative reference figures" className="mb-8">
        The figures on this page are illustrative. The PE Valuation Engine does not
        read them and keeps its own advance-rate and sector tables, which differ from
        these. Each collateral assessment on Collateral Monitoring returns the
        advance-rate guidance the engine applied to that fund.
      </Alert>

      <div className="space-y-4">
        <ConfigSection
          id="advance-rates"
          title="Advance Rate Ranges by Strategy"
          description="Illustrative advance-rate ranges for PE fund strategies"
          isExpanded={expanded.has('advance-rates')}
          onToggle={() => toggleSection('advance-rates')}
        >
          <DataGrid<AdvanceRateRowState>
            data={ADVANCE_RATE_REFERENCE}
            columns={ADVANCE_RATE_COLUMNS}
            enablePagination={false}
            minTableWidth={560}
            getRowId={(row) => row.strategy}
          />
        </ConfigSection>

        <ConfigSection
          id="cha-tiers"
          title="Collateral Health Alert Tiers"
          description="Illustrative tier levels above a position's recommended advance rate, and the response at each tier"
          isExpanded={expanded.has('cha-tiers')}
          onToggle={() => toggleSection('cha-tiers')}
        >
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {CHA_TIER_REFERENCE.map((row) => (
                <div key={row.tier} className="border border-recessed rounded-sm p-5">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <span className="text-sm font-sans font-semibold text-primary">{row.tier}</span>
                    <span
                      className={`inline-flex px-3 py-1 text-xs font-mono font-semibold rounded-sm leading-normal tabular-nums ${row.tone}`}
                    >
                      {row.level}
                    </span>
                  </div>
                  <p className="text-sm font-sans text-secondary leading-relaxed">{row.description}</p>
                </div>
              ))}
              <div className="border border-recessed rounded-sm p-5">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <span className="text-sm font-sans font-semibold text-primary">Cure period</span>
                  <span className="inline-flex px-3 py-1 text-xs font-mono font-semibold rounded-sm leading-normal bg-quaternary/10 text-quaternary">
                    {CURE_PERIOD_REFERENCE}
                  </span>
                </div>
                <p className="text-sm font-sans text-secondary leading-relaxed">
                  Time the member has to restore the position before it moves to Red.
                </p>
              </div>
            </div>

            <div className="mt-8 p-5 bg-elevated rounded-sm">
              <p className="text-label-xs font-sans font-semibold uppercase text-tertiary mb-4">
                Example: a {formatPercent(EXAMPLE_ADVANCE)} advance rate
              </p>
              <div
                className="relative h-8 bg-surface rounded-sm overflow-hidden border border-recessed"
                role="img"
                aria-label={`Green below ${formatPercent(EXAMPLE_YELLOW)}, Yellow from ${formatPercent(EXAMPLE_YELLOW)} to ${formatPercent(EXAMPLE_ORANGE)}, Orange from ${formatPercent(EXAMPLE_ORANGE)} to ${formatPercent(EXAMPLE_RED)}, Red from ${formatPercent(EXAMPLE_RED)}`}
              >
                <div className="absolute inset-y-0 left-0 bg-growth/20" style={{ width: pct(EXAMPLE_YELLOW) }} />
                <div
                  className="absolute inset-y-0 bg-warning/20"
                  style={{ left: pct(EXAMPLE_YELLOW), width: pct(EXAMPLE_ORANGE - EXAMPLE_YELLOW) }}
                />
                <div
                  className="absolute inset-y-0 bg-warning/40"
                  style={{ left: pct(EXAMPLE_ORANGE), width: pct(EXAMPLE_RED - EXAMPLE_ORANGE) }}
                />
                <div
                  className="absolute inset-y-0 bg-error/30"
                  style={{ left: pct(EXAMPLE_RED), width: pct(1 - EXAMPLE_RED) }}
                />
                <div
                  className="absolute inset-y-0 w-px bg-primary"
                  style={{ left: pct(EXAMPLE_ADVANCE) }}
                />
              </div>
              <ul className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-xs font-mono text-secondary tabular-nums md:grid-cols-5">
                <li>Advance rate {formatPercent(EXAMPLE_ADVANCE)}</li>
                <li>Green below {formatPercent(EXAMPLE_YELLOW)}</li>
                <li>Yellow {formatPercent(EXAMPLE_YELLOW)} to {formatPercent(EXAMPLE_ORANGE)}</li>
                <li>Orange {formatPercent(EXAMPLE_ORANGE)} to {formatPercent(EXAMPLE_RED)}</li>
                <li>Red {formatPercent(EXAMPLE_RED)} and above</li>
              </ul>
            </div>
          </div>
        </ConfigSection>

        <ConfigSection
          id="sector-benchmarks"
          title="Sector Benchmark Reference"
          description="Illustrative sector multiples for comparable-based NAV estimation"
          isExpanded={expanded.has('sector-benchmarks')}
          onToggle={() => toggleSection('sector-benchmarks')}
        >
          <DataGrid<SectorRowState>
            data={SECTOR_REFERENCE}
            columns={SECTOR_COLUMNS}
            enablePagination={false}
            minTableWidth={560}
            getRowId={(row) => row.sector}
          />
          <p className="px-6 py-4 bg-elevated/50 text-sm font-sans text-secondary">
            The private discount is the illiquidity discount applied to a NAV estimate.
            These are indicative references; a valuation uses fund-specific data.
          </p>
        </ConfigSection>
      </div>
    </div>
  );
}
